import type { AdminImpersonation, ImpersonationStart } from './api';

export const SUPPORT_HEADER = 'X-Stratplan-Support-Session';
export const SUPPORT_STORAGE_KEY = 'stratplan:support-session:v1';
export const SUPPORT_CHANGED = 'stratplan:support-session-changed';
export interface SupportBinding {
  userId: string;
  sessionId: string;
}
interface StoredSupport {
  binding: SupportBinding;
  credential: string;
  session: AdminImpersonation;
}
let stored: StoredSupport | null = null;
let scopeRevision = 0;
let transitionBlocked = false;
export function allowAdministratorRequests() {
  transitionBlocked = false;
}
export function captureSupportScope() {
  return scopeRevision;
}
export function isSupportActivity() {
  return !!stored || hasSavedSupportSession() || !!(window as any).Clerk?.session?.actor;
}
export function hasSavedSupportSession() {
  try {
    return sessionStorage.getItem(SUPPORT_STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

function browserBinding(): SupportBinding | null {
  const clerk = (window as any).Clerk;
  return clerk?.user?.id && clerk?.session?.id
    ? { userId: clerk.user.id, sessionId: clerk.session.id }
    : null;
}
function matches(a: SupportBinding, b: SupportBinding) {
  return a.userId === b.userId && a.sessionId === b.sessionId;
}
export function clearSupportSession() {
  const existed = !!stored || hasSavedSupportSession();
  stored = null;
  try {
    sessionStorage.removeItem(SUPPORT_STORAGE_KEY);
  } catch {
    /* No persistent credential can be read in this environment. */
  }
  if (existed) {
    scopeRevision += 1;
    transitionBlocked = true;
  }
  if (existed) window.dispatchEvent(new Event(SUPPORT_CHANGED));
}
export function restoreSupportSession(binding: SupportBinding): AdminImpersonation | null {
  try {
    const raw = sessionStorage.getItem(SUPPORT_STORAGE_KEY);
    if (!raw) {
      stored = null;
      return null;
    }
    const value = JSON.parse(raw) as StoredSupport;
    if (
      !value.binding ||
      !matches(value.binding, binding) ||
      typeof value.session?.id !== 'string' ||
      !value.session.id ||
      typeof value.session?.target?.name !== 'string' ||
      typeof value.session?.target?.email !== 'string' ||
      typeof value.session?.target?.clerkId !== 'string' ||
      value.session?.actor?.clerkId !== binding.userId ||
      typeof value.session?.actor?.name !== 'string' ||
      typeof value.session?.reason !== 'string' ||
      !/^[A-Za-z0-9_-]{43}$/.test(value.credential) ||
      value.session?.mode !== 'app' ||
      value.session.originalSessionId !== binding.sessionId ||
      !Number.isFinite(Date.parse(value.session.expiresAt)) ||
      Date.parse(value.session.expiresAt) <= Date.now()
    ) {
      clearSupportSession();
      return null;
    }
    stored = value;
    transitionBlocked = false;
    return value.session;
  } catch {
    clearSupportSession();
    return null;
  }
}
export function installSupportSession(
  result: ImpersonationStart,
  binding: SupportBinding,
  session: AdminImpersonation
) {
  const actual = browserBinding();
  if (
    !actual ||
    !matches(actual, binding) ||
    result.mode !== 'app' ||
    result.originalSessionId !== binding.sessionId ||
    session.mode !== 'app' ||
    session.id !== result.id ||
    session.actor.clerkId !== binding.userId ||
    !/^[A-Za-z0-9_-]{43}$/.test(result.credential)
  )
    throw new Error('Your administrator session changed. Open a new support session.');
  transitionBlocked = false;
  stored = { binding, credential: result.credential, session };
  sessionStorage.setItem(SUPPORT_STORAGE_KEY, JSON.stringify(stored));
}
// Install only the credential while the server validates its effective identity. It is never
// included in public or foreign-origin requests, even if a caller supplies this header itself.
export function prepareSupportSession(result: ImpersonationStart, binding: SupportBinding) {
  if (
    result.mode !== 'app' ||
    result.originalSessionId !== binding.sessionId ||
    !/^[A-Za-z0-9_-]{43}$/.test(result.credential)
  )
    throw new Error('Your administrator session changed. Open a new support session.');
  scopeRevision += 1;
  transitionBlocked = false;
  stored = {
    binding,
    credential: result.credential,
    session: { mode: 'app', id: result.id, expiresAt: result.expiresAt } as AdminImpersonation,
  };
}
export function internalApiHeaders(
  destination: string | URL,
  headers?: HeadersInit,
  isPublic = false,
  requestScope = scopeRevision
): Headers {
  const result = new Headers(headers);
  result.delete(SUPPORT_HEADER);
  if (isPublic) return result;
  if (requestScope !== scopeRevision)
    throw new Error('Support access changed. Retry from the current workspace.');
  if (transitionBlocked)
    throw new Error('Support access is closing. Retry from the current workspace.');
  if (!stored) return result;
  const binding = browserBinding();
  if (
    !binding ||
    !matches(stored.binding, binding) ||
    Date.parse(stored.session.expiresAt) <= Date.now()
  ) {
    clearSupportSession();
    throw new Error('Support access expired or changed. Return to administration.');
  }
  const api = new URL(import.meta.env.VITE_API_URL || '/api', window.location.origin);
  const url = new URL(destination, window.location.origin);
  const prefix = api.pathname.replace(/\/$/, '');
  if (
    url.origin === api.origin &&
    !url.username &&
    !url.password &&
    (url.pathname === prefix || url.pathname.startsWith(`${prefix}/`))
  )
    result.set(SUPPORT_HEADER, stored.credential);
  return result;
}

// The one cleanup exception uses the original administrator identity and only the audited
// abort endpoint. It cannot authorize general business calls during a scope transition.
export function originalAdministratorCleanupHeaders(
  destination: string,
  headers: HeadersInit,
  binding: SupportBinding
) {
  const actual = browserBinding();
  const api = new URL(import.meta.env.VITE_API_URL || '/api', window.location.origin);
  const url = new URL(destination, window.location.origin);
  const prefix = api.pathname.replace(/\/$/, '');
  const relative = url.pathname.slice(prefix.length);
  if (
    !actual ||
    !matches(actual, binding) ||
    url.origin !== api.origin ||
    !url.pathname.startsWith(`${prefix}/`) ||
    !/^\/admin\/impersonation\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/abort$/i.test(
      relative
    ) ||
    url.search ||
    url.hash ||
    url.username ||
    url.password
  )
    throw new Error('Administrator cleanup could not be verified. Return to administration.');
  const result = new Headers(headers);
  result.delete(SUPPORT_HEADER);
  return result;
}
